// Protocol training constants + helpers.
//
// What lives here:
//   - `RUN_PLAN` — base running progression (Z2 build → quality → taper). The
//     operator's tracked fallback; the coach's plan_run_arc overlays it toward
//     the half-marathon goal when a coach call exists.
//   - `workoutTypeToKind` — workout type → sport icon kind. The week's
//     layout is NOT here: it comes from the operator's weekly_commitments
//     and the coach's applied workout_log rows (see lib/day-data.ts).
//   - `roundToWholeKg` — Speediance hardware-aware load rounding
//   - `SPEEDIANCE_MATCH_THRESHOLD`, `speedianceTopMatches` — catalog search
//     helpers (exercise-name → Speediance library entry)

import type { SportIconKind } from '../components/SportIcon';

// ---- TYPES ----

/**
 * The fixed daily anchors from CLAUDE.md. The day timeline pins to these.
 * Mornings (workout) and meal slots; meal payloads live in `lib/day-data.ts`.
 */
export const WORKOUT_TIME = '09:00';

export type RunWeek = {
  week: number;
  easy: string;
  long: string;
  notes: string;
};

// The synthesised shape a single day's plan renders from — built from a
// workout_log row, a coach prescription, or the weekday's commitment.
export type WeekDay = {
  day: 'Mon' | 'Tue' | 'Wed' | 'Thu' | 'Fri' | 'Sat' | 'Sun';
  plan: string;
  kind: SportIconKind;
  // 'Strength' is every strength day: an operator commitment and the coach's
  // applied strength sessions alike (the exercise list comes
  // from daily_strength_prescription). 'Mixed' is coach-only (any other
  // non-run/padel/rest coach activity) and never offered in the operator's
  // slot picker — see ledger.ts PICKABLE_SESSION_TYPES. Coach origin itself
  // lives on WorkoutRow.source, not on the type. Day's mutations pass this
  // type back to the server unchanged so workout_log writes land on the
  // correct (date, type) composite key.
  type:
    | 'Strength' | 'Mixed' | 'Padel' | 'Rest'
    | 'Run-Easy' | 'Run-Long' | 'Run-Tempo' | 'Run-Intervals' | 'Run-Recovery';
};

// Single source of truth: a workout type → its sport icon kind.
// Strength → strength; all Run-* → run; Padel → padel; Rest → rest;
// Mixed → mixed.
export function workoutTypeToKind(type: WeekDay['type']): SportIconKind {
  if (type === 'Strength') return 'strength';
  if (type === 'Padel') return 'padel';
  if (type === 'Rest') return 'rest';
  if (type === 'Mixed') return 'mixed';
  return 'run'; // all Run-* subtypes
}

// ---- SPEEDIANCE INTEGRATION ----

// Catalog-search threshold below which a match is not worth suggesting.
export const SPEEDIANCE_MATCH_THRESHOLD = 0.2;

export type SpeedianceMatch<T> = { entry: T; score: number };

// ---- Catalog search (exercise name → Speediance library entry) ----

const _STOPWORDS = new Set(['the', 'a', 'an', 'of', 'to', 'with', 'and', 'or', 'for', 'from', 'on', 'at', 'in', 'by']);

function _tokenize(s: string): Set<string> {
  return new Set(
    String(s || '')
      .toLowerCase()
      .replace(/[^a-z0-9 ]/g, ' ')
      .split(/\s+/)
      .filter((t) => t && !_STOPWORDS.has(t)),
  );
}

// Jaccard similarity over name tokens (stopwords dropped).
function speedianceSimilarity(a: string, b: string): number {
  const ta = _tokenize(a);
  const tb = _tokenize(b);
  if (ta.size === 0 || tb.size === 0) return 0;
  let intersection = 0;
  for (const t of ta) if (tb.has(t)) intersection++;
  return intersection / (ta.size + tb.size - intersection);
}

/** The `n` library entries whose name best matches `query`, best first. */
export function speedianceTopMatches<T extends { name: string }>(
  query: string,
  library: T[],
  n = 5,
): SpeedianceMatch<T>[] {
  const scored: SpeedianceMatch<T>[] = library.map((entry) => ({
    entry,
    score: speedianceSimilarity(query, entry.name),
  }));
  scored.sort((a, b) => b.score - a.score);
  return scored.slice(0, n);
}

// Round to the nearest Speediance hardware step. The device's COMBINED load is
// whole-kg only — a half-kg combined target (e.g. 12.5) is unloadable and the
// machine clamps it to 12. (The per-handle 0.5 seen in read-back, e.g. 8.5+8.5,
// is just the device splitting an odd whole-kg combined load across two handles
// for display; each handle is still whole-kg.) So round to whole kg, not 0.5 —
// otherwise the pushed target can never be hit and the target-vs-actual drifts.
export function roundToWholeKg(kg: number): number {
  return Math.round(kg);
}

// ---- Running plan ----

export const RUN_PLAN: RunWeek[] = [
  { week: 1, easy: '25 min @ Z2', long: '35 min @ Z2', notes: 'Walk if HR drifts above Z2. Patience wins.' },
  { week: 2, easy: '28 min @ Z2', long: '40 min @ Z2', notes: 'Same intent. Build the engine.' },
  { week: 3, easy: '30 min @ Z2', long: '42 min @ Z2', notes: 'Try nose-only breathing for 5 min.' },
  { week: 4, easy: '30 min @ Z2', long: '45 min @ Z2', notes: 'Add 4× 20s strides after easy.' },
  { week: 5, easy: '32 min @ Z2', long: '45 min @ Z2', notes: '5× 20s strides post-easy.' },
  { week: 6, easy: '32 min @ Z2', long: '48 min @ Z2', notes: '6× 20s strides.' },
  { week: 7, easy: '10min Z2 + 4×3min Z3 (2min Z1) + 10min Z2', long: '50 min @ Z2', notes: 'First real quality session.' },
  { week: 8, easy: '10min Z2 + 5×3min Z3 (2min Z1) + 10min Z2', long: '50 min @ Z2', notes: 'Push the tempo a notch.' },
  { week: 9, easy: 'Intervals: 5×800m @ Z4 (90s Z1 jog)', long: '55 min @ Z2', notes: 'Speed work begins.' },
  { week: 10, easy: 'Intervals: 6×800m @ Z4 (90s Z1 jog)', long: '55 min @ Z2', notes: 'Last big interval week.' },
  { week: 11, easy: '20min continuous @ Z3 (sandwich w/ 10min Z2)', long: '45 min @ Z2', notes: 'Taper begins.' },
  { week: 12, easy: '20 min very easy Z2 (Mon)', long: 'Long run 60 min @ Z2 — half-marathon base', notes: 'Trust the work.' },
];

// Run-arc bar scaling, shared by the desktop + mobile arc cards so they render
// identically. `runArcIntensity` maps the static RUN_PLAN's phase shape onto the
// same 1–10 scale the coach arc uses; `runArcBarHeight` turns a 1–10 intensity
// into a bar height in px (1→~20px, 10→70px).
export function runArcIntensity(week: number): number {
  if (week <= 6) return week; // 1..6 base build
  if (week <= 10) return 6 + (week - 6); // 7..10 quality (week 7 = 7, above the base)
  if (week === 11) return 4; // taper
  return 9; // test/peak
}
export function runArcBarHeight(intensity: number): number {
  return 14 + Math.max(1, Math.min(10, intensity)) * 5.6;
}

// ---- HR zones (Runna-aligned, Tanaka HRmax) ----
//
// Matches CLAUDE.md's published zones byte-for-byte and the backend's
// zonesFor (apps/protocol-api/src/lib/fit-zones.ts), so the prescription
// panels here and the FIT run-card zone bars agree. Boundaries are
// floor(percent × HRmax) at 65 / 81 / 89 / 97% (NOT 50/60/70/80/90, and NOT
// round); at age 45 (HRmax 177) this yields the documented bands:
//   Z1 0–114 · Z2 115–142 · Z3 143–156 · Z4 157–170 · Z5 171+
//
// `range` is displayed INCLUSIVE (lo–hi bpm). The lower of each zone is
// floor(pct × HRmax); the upper is the next zone's lower minus 1 (Z5 caps at
// HRmax). The backend's zonesFor uses lo-inclusive / hi-EXCLUSIVE [lo, nextLo)
// for membership; the only frontend consumers here are display strings, so the
// inclusive form is what we want.
export const hrMax = (age: number): number => Math.round(208 - 0.7 * (age || 38));

export type HRZone = { name: 'Z1' | 'Z2' | 'Z3' | 'Z4' | 'Z5'; label: string; range: [number, number] };

// Lower-bound percents of HRmax for Z1..Z5 (Runna's published splits).
const ZONE_PCT_LOWERS = [0, 0.65, 0.81, 0.89, 0.97] as const;

export const hrZones = (age: number): HRZone[] => {
  const max = hrMax(age);
  const lo = ZONE_PCT_LOWERS.map((p) => Math.floor(p * max));
  // Inclusive upper = next zone's lower − 1; Z5 caps at HRmax.
  const range = (i: number): [number, number] => [
    lo[i]!,
    i < 4 ? lo[i + 1]! - 1 : max,
  ];
  return [
    { name: 'Z1', label: 'Recovery', range: range(0) },
    { name: 'Z2', label: 'Endurance', range: range(1) },
    { name: 'Z3', label: 'Tempo', range: range(2) },
    { name: 'Z4', label: 'Threshold', range: range(3) },
    { name: 'Z5', label: 'Anaerobic', range: range(4) },
  ];
};
